// src/components/UpsellScheduleManager.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Typography,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
} from "@mui/material";

import Cookies from "js-cookie";
import { useNotification } from "../contexts/NotificationContext";
import { getBackendBaseUrl } from "../utils/envUtils";
import { upgradeCostsMatrix, getAllowedUpgradeFroms, getAllowedUpgrades } from "../utils/roomCodes";
import { MAIN_ERROR_MESSAGES } from "../constants/errorMessages";
import { PROFILE_OPTIONS } from "../constants/selectOptions";

import ProfileSelect from "./ProfileSelect";
import PreconditionsMapEditor from "./scheduler/PreconditionsMapEditor";
import RatePlansField from "./scheduler/RatePlansField";
import ChipMultiSelectControl from "./scheduler/ChipMultiSelectControl";
import { useHistory } from "react-router-dom";

import { safeParseArray, safeParseObject, compactIntegerMap } from "../utils/jsonSafe";


// ---------- constants ----------
const ITEM_HEIGHT = 38;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 6 + ITEM_PADDING_TOP,
      width: 320,
    },
  },
};

export default function UpsellScheduleManager({ initialSchedule }) {
  // ---------- env / endpoints ----------
  const HOST = getBackendBaseUrl();
  const SCHEDULE_ENDPOINT = `${HOST}/api/v1/upsell-schedule`;
  const PROFILE_ENDPOINT = `${HOST}/api/v1/room-types/upsell`;
  const RATE_PLAN_ENDPOINT = `${HOST}/api/v1/rate-plans`;
  const token = Cookies.get("jwtToken");

  // ---------- notifications & nav ----------
  const { setIsOpen, setMessage, setIsFailed } = useNotification();
  const history = useHistory();

  // ---------- async-loaded selector data ----------
  const [selectedProfile, setSelectedProfile] = useState("");
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [rooms, setRooms] = useState();

  const [fetchedProfileOptions, setFetchedProfileOptions] = useState([]);
  const [fetchedRatePlanOptions, setFetchedRatePlanOptions] = useState([]);

  // Derived from profile
  const [upsellFromOptions, setUpsellFromOptions] = useState([]);
  const [upsellToOptions, setUpsellToOptions] = useState([]);

  // ---------- schedule form state ----------
  const [newSchedule, setNewSchedule] = useState({
    title: "",
    roomTypes: "[]",
    ratePlans: "[]",
    daysPrecedingArrival: 0,
    preconditions: "{}",
    preconditionsMap: {},
    isActive: false,
    roomTypesList: [],
    ratePlansList: [],
    upgradeTo: "[]",
    upgradeToList: [],
  });

  // ---------- static / derived room-code options ----------
  const upgradeFromOptionsStatic = useMemo(
    () => Object.keys(upgradeCostsMatrix?.beaconGrand || {}),
    []
  );

  const upgradeToOptionsStatic = useMemo(() => {
    const nested = Object.values(upgradeCostsMatrix?.beaconGrand || {})
      .flatMap((group) => Object.keys(group || {}));
    return Array.from(new Set(nested)).sort();
  }, []);

  const allowedUpgradeToOptions = useMemo(
    () => getAllowedUpgrades(newSchedule.roomTypesList || []),
    [newSchedule.roomTypesList]
  );

  const allRoomCodes = useMemo(
    () => Array.from(new Set([...upgradeFromOptionsStatic, ...upgradeToOptionsStatic])).sort(),
    [upgradeFromOptionsStatic, upgradeToOptionsStatic]
  );

  const roomTypeOptions = upsellFromOptions.length ? upsellFromOptions : upgradeFromOptionsStatic;

  // Keep upgradeToList within allowed options
  useEffect(() => {
    setNewSchedule((prev) => {
      const current = prev.upgradeToList || [];
      const next = current.filter((c) => allowedUpgradeToOptions.includes(c));
      return current.length === next.length ? prev : { ...prev, upgradeToList: next };
    });
  }, [allowedUpgradeToOptions]);

  // Keep ratePlansList within allowed options whenever options change
  useEffect(() => {
    setNewSchedule((prev) => {
      const allowed = new Set(fetchedRatePlanOptions || []);
      const next = (prev.ratePlansList || []).filter((v) => allowed.has(v));
      return next.length === (prev.ratePlansList || []).length ? prev : { ...prev, ratePlansList: next };
    });
  }, [fetchedRatePlanOptions]);

  // ---------- hydrate from initialSchedule ----------
  useEffect(() => {
    if (!initialSchedule) return;

    setNewSchedule((prev) => ({
      ...prev,
      id: initialSchedule.id,
      title: initialSchedule.title ?? "",
      isActive: !!initialSchedule.isActive,
      daysPrecedingArrival: Number(initialSchedule.daysPrecedingArrival ?? 0),

      roomTypes: initialSchedule.roomTypes ?? "[]",
      upgradeTo: initialSchedule.upgradeTo ?? "[]",
      ratePlans: initialSchedule.ratePlans ?? "[]",
      preconditions: initialSchedule.preconditions ?? "{}", // wire

      roomTypesList: Array.isArray(initialSchedule.roomTypesList)
        ? initialSchedule.roomTypesList
        : safeParseArray(initialSchedule.roomTypes),

      upgradeToList: Array.isArray(initialSchedule.upgradeToList)
        ? initialSchedule.upgradeToList
        : safeParseArray(initialSchedule.upgradeTo),

      ratePlansList: Array.isArray(initialSchedule.ratePlansList)
        ? initialSchedule.ratePlansList
        : safeParseArray(initialSchedule.ratePlans),

      preconditionsMap:
        initialSchedule.preconditionsMap && typeof initialSchedule.preconditionsMap === "object"
          ? initialSchedule.preconditionsMap
          : safeParseObject(initialSchedule.preconditions),
    }));
  }, [initialSchedule]);

  // ---------- profile & rate plan loader ----------
  const handleSelectProfile = async (e) => {
    const profileKey = e.target.value;
    setSelectedProfile(profileKey);
    setIsProfileLoading(true);

    setFetchedProfileOptions([]);
    setUpsellFromOptions([]);
    setUpsellToOptions([]);
    setFetchedRatePlanOptions([]);

    const notificationName = profileKey === "sandbox" ? "Opera Sandbox" : "Beacon Grand";

    try {
      const [profileResponse, ratePlanResponse] = await Promise.all([
        fetch(`${PROFILE_ENDPOINT}?env=${profileKey.toLowerCase()}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${RATE_PLAN_ENDPOINT}?env=${profileKey.toLowerCase()}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (profileResponse.status === 401) throw new Error(profileResponse.status);
      if (!profileResponse.ok) throw new Error("Network response was not ok");

      const profileData = await profileResponse.json();
      const codes = profileData.map((item) => item.code);

      const ratePlansRaw = await ratePlanResponse.json();
      const planCodes = (Array.isArray(ratePlansRaw) ? ratePlansRaw : [])
        .map((r) => (typeof r === "string" ? r : r?.code ?? r?.id ?? ""))
        .filter(Boolean);
      const dedupedPlanCodes = Array.from(new Set(planCodes));

      setRooms(profileData);
      setFetchedProfileOptions(codes);
      setUpsellFromOptions(getAllowedUpgradeFroms(codes));
      setFetchedRatePlanOptions(dedupedPlanCodes);

      setIsOpen(true);
      setIsFailed(false);
      setMessage(`Profile is now set to ${notificationName}`);
    } catch (error) {
      const errorMessage = MAIN_ERROR_MESSAGES[error?.message] || MAIN_ERROR_MESSAGES.default;
      setIsOpen(true);
      setIsFailed(true);
      setMessage(errorMessage);
    } finally {
      setIsProfileLoading(false);
    }
  };

  // ---------- validation ----------
  function validatePreconditionsMap(map) {
    const allowed = new Set(allRoomCodes);
    return Object.entries(map || {}).every(([k, v]) => allowed.has(k) && Number.isInteger(v));
  }

  // ---------- submit ----------
  const isEdit = Boolean(newSchedule?.id);

  async function submitSchedule() {
    try {
      if (!validatePreconditionsMap(newSchedule.preconditionsMap)) {
        throw new Error("Invalid preconditions: keys must be valid room codes and values integers.");
      }

      const payload = {
        ...newSchedule,
        roomTypes: JSON.stringify(newSchedule.roomTypesList),
        ratePlans: JSON.stringify(newSchedule.ratePlansList),
        upgradeTo: JSON.stringify(newSchedule.upgradeToList),
        preconditions: JSON.stringify(compactIntegerMap(newSchedule.preconditionsMap)),
      };

      const url = isEdit ? `${SCHEDULE_ENDPOINT}/${newSchedule.id}` : `${SCHEDULE_ENDPOINT}`;
      const method = isEdit ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(isEdit ? "Failed to update schedule" : "Failed to create schedule");

      history.push("/upsell/schedules");
    } catch (err) {
      setIsOpen(true);
      setIsFailed(true);
      setMessage(err.message || "Operation failed");
    }
  }

  // ---------- UI ----------
  return (
    <div style={{ padding: 24 }}>
      <Typography variant="h4" gutterBottom>
        {isEdit ? "Edit Upsell Schedule" : "Create Upsell Schedule"}
      </Typography>

      <Card sx={{ mb: 3 }}>
        <ProfileSelect
          label="Choose a profile"
          handleSelectValue={handleSelectProfile}
          isItemLoading={isProfileLoading}
          selectedValue={selectedProfile}
          options={PROFILE_OPTIONS}
        />
      </Card>

      <Card>
        <CardHeader title={isEdit ? "Edit Schedule" : "New Schedule"} />
        <CardContent>
          {/* Active */}
          <FormControlLabel
            control={
              <Checkbox
                checked={newSchedule.isActive}
                onChange={(e) => setNewSchedule((p) => ({ ...p, isActive: e.target.checked }))}
              />
            }
            label="Active"
          />

          {/* Title */}
          <TextField
            label="Title"
            fullWidth
            margin="dense"
            value={newSchedule.title}
            onChange={(e) => setNewSchedule((p) => ({ ...p, title: e.target.value }))}
          />

          {/* Upgrade From */}
          <ChipMultiSelectControl
            label="Upgrade From"
            labelId="upgrade-from-label"
            value={newSchedule.roomTypesList}
            onChange={(arr) => setNewSchedule((p) => ({ ...p, roomTypesList: arr }))}
            options={roomTypeOptions.length ? roomTypeOptions : upgradeFromOptionsStatic}
            MenuProps={MenuProps}
          />

          {/* Upgrade To (filtered by allowedUpgradeToOptions) */}
          <ChipMultiSelectControl
            label="Upgrade To"
            labelId="upgrade-to-label"
            value={newSchedule.upgradeToList}
            onChange={(arr) => setNewSchedule((p) => ({ ...p, upgradeToList: arr }))}
            options={allowedUpgradeToOptions}
            MenuProps={MenuProps}
            disabled={allowedUpgradeToOptions.length === 0}
          />
          {allowedUpgradeToOptions.length === 0 && (
            <Typography variant="caption" sx={{ mt: 0.5, display: "block", opacity: 0.7 }}>
              Select at least one “Upgrade From” room code to see allowed upgrades.
            </Typography>
          )}

          {/* Rate Plans */}
          <RatePlansField
            options={fetchedRatePlanOptions}
            value={newSchedule.ratePlansList}
            onChange={(next) => setNewSchedule((p) => ({ ...p, ratePlansList: next }))}
          />

          {/* Days */}
          <TextField
            type="number"
            label="Days Preceding Arrival"
            fullWidth
            margin="dense"
            value={newSchedule.daysPrecedingArrival}
            onChange={(e) =>
              setNewSchedule((p) => ({
                ...p,
                daysPrecedingArrival: parseInt(e.target.value || "0", 10),
              }))
            }
          />

          {/* Preconditions */}
          <PreconditionsMapEditor
            value={newSchedule.preconditionsMap}
            onChange={(nextMap) => setNewSchedule((p) => ({ ...p, preconditionsMap: nextMap }))}
            allowedKeys={allRoomCodes}
            title="Inventory Requirements"
          />
        </CardContent>

        <CardActions>
          <Button variant="contained" onClick={submitSchedule}>
            {isEdit ? "Save Changes" : "Create"}
          </Button>
        </CardActions>
      </Card>
    </div>
  );
}
